import { create } from "zustand";
import {
  MarketplaceProductsState,
  ProductEcom,
  ProductEcomCloneInput,
  ProductEcomListItem,
  ProductEcomQuery,
} from "../types/marketplace_product";
import { Channel } from "../types/channel";
import { useFetcher } from "./fetcher";
import { Paging } from "../types/paging";
import { sortQueryFromOrderByRecord } from "../utils/query";

const SHOW_FILTER_KEY = "showFilter:marketplaceProducts";
const DEFAULT_LIMIT = 20;

export const useMarketplaceProductsStore = create<MarketplaceProductsState>(
  (set, get) =>
    ({
      items: [],
      total: null,
      query: null,
      limit: DEFAULT_LIMIT,
      showFilter: (() => {
        try {
          const v = localStorage.getItem(SHOW_FILTER_KEY);
          return v !== null ? JSON.parse(v) : true;
        } catch {
          return true;
        }
      })(),
      search: async (query?: ProductEcomQuery, fetch?: boolean) => {
        set(() => ({ query: query ?? null }));
        if (fetch !== false) {
          await get().fetch(true);
        }
      },
      fetch: async (
        reset: boolean
      ): Promise<Paging<ProductEcomListItem> | null> => {
        const { items, limit, query, total } = get();
        if (!reset && total !== null && total <= items.length) {
          return null;
        }
        const sort = sortQueryFromOrderByRecord(query?.order_by);
        const params: any = query
          ? {
              ...query,
              order_by: undefined,
              sort,
              limit,
              offset: reset ? 0 : items.length,
            }
          : { limit, offset: reset ? 0 : items.length };
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get<Paging<ProductEcomListItem>>("product-ecoms", {
            params,
            paramsSerializer: { indexes: null },
          });
        set(() => ({
          items: [...(reset ? [] : items), ...(res?.data?.items ?? [])],
          total: res?.data?.total ?? null,
        }));
        return res?.data ?? null;
      },
      get: async (id: string): Promise<ProductEcom | null> => {
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get<ProductEcom>(`product-ecoms/${id}`);
        return res?.data ?? null;
      },
      delete: async (id: string): Promise<void> => {
        await useFetcher
          .getState()
          .omsDsFetcher.delete(`product-ecoms/${id}`);
        set((s) => ({
          items: s.items.filter((it) => it.id !== id),
          total: s.total !== null ? Math.max(0, s.total - 1) : null,
        }));
      },
      clone: async (
        id: string,
        input?: ProductEcomCloneInput
      ): Promise<ProductEcom | null> => {
        const res = await useFetcher
          .getState()
          .omsDsFetcher.post<ProductEcom>(
            `product-ecoms/${id}/clone`,
            input ?? {}
          );
        return res?.data ?? null;
      },
      // Channels visible to every workspace that owns or shares the product.
      // Backs the marketplace-products/{id} "Add Channel" picker in /ws/global
      // — the regular channels.fetchAll returns every channel in the org
      // when called from /ws/global, which surfaces channels the product
      // can't actually publish to. Inside /ws/<id> the channels store is
      // already workspace-scoped, so the editor only calls this in global.
      listAccessibleChannels: async (id: string): Promise<Channel[]> => {
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get<Channel[]>(
            `product-ecoms/${id}/accessible-channels`
          );
        return res?.data ?? [];
      },
      clear: () => {
        set(() => ({ items: [], total: null, query: null }));
      },
      setShowFilter: (value: boolean) => {
        set(() => ({ showFilter: value }));
        try {
          localStorage.setItem(SHOW_FILTER_KEY, JSON.stringify(value));
        } catch {
          // ignore write errors (e.g. private mode)
        }
      },
    } as MarketplaceProductsState)
);
